import { type ReactNode, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/utils';
import { AppForm, ConfigurationForm, UsageForm } from '@/components/profile/ProposalAddForms';
import {
  CONFIGURATION_AREA_LABELS,
  USAGE_PLACE_LABELS,
  USAGE_RHYTHM_LABELS,
  addProposalEntry,
  removeProposalEntry,
  setProposalDecision,
} from '@/lib/company-qualification';
import type { CompanyQualificationSheet, ProposalDecision } from '@shared/types/company-qualification';

type ProposalList = 'configuration' | 'apps' | 'usage';

const DECISION_TONES: Record<ProposalDecision, string> = {
  proposed: 'text-domino-inkSoft',
  accepted: 'text-emerald-700',
  dismissed: 'text-stone-400 line-through',
};

interface EntryProps { decision: ProposalDecision; onDecision: (decision: ProposalDecision) => void; onRemove: () => void; children: ReactNode }

const Entry = ({ decision, onDecision, onRemove, children }: EntryProps) => (
  <li className="group flex items-start gap-3 py-2">
    <div className={cn('min-w-0 flex-1 text-sm', decision === 'dismissed' && 'opacity-60')}>{children}</div>
    <div className="flex shrink-0 items-center gap-1 text-xs">
      {(['accepted', 'dismissed'] as const).map((value) => (
        <button
          key={value}
          type="button"
          onClick={() => onDecision(decision === value ? 'proposed' : value)}
          className={cn('rounded-full px-2 py-0.5 ring-1 ring-domino-cardBorder', decision === value ? DECISION_TONES[value] + ' bg-domino-muted' : 'text-domino-inkSoft')}
        >
          {value === 'accepted' ? 'Accepter' : 'Écarter'}
        </button>
      ))}
      <button type="button" aria-label="Supprimer" onClick={onRemove} className="rounded p-1 text-domino-inkSoft opacity-0 hover:bg-domino-muted group-hover:opacity-100 focus:opacity-100">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  </li>
);

interface Props { sheet: CompanyQualificationSheet; onChange: (sheet: CompanyQualificationSheet) => void }

/** The restitution: configuration, apps and how to use Domino, then success, first proof and what is left out. */
export const QualificationProposalPanel = ({ sheet, onChange }: Props) => {
  const [adding, setAdding] = useState<ProposalList | null>(null);
  const { proposal } = sheet;
  const decide = (list: ProposalList, id: string) => (decision: ProposalDecision) => onChange(setProposalDecision(sheet, list, id, decision));
  const remove = (list: ProposalList, id: string) => () => onChange(removeProposalEntry(sheet, list, id));
  const header = (list: ProposalList, title: string, hint: string) => (
    <div className="flex items-baseline justify-between gap-3">
      <div>
        <h4 className="text-sm font-semibold text-domino-ink">{title}</h4>
        <p className="text-xs text-domino-inkSoft">{hint}</p>
      </div>
      {adding !== list && <Button variant="ghost" size="sm" onClick={() => setAdding(list)}>+ Ajouter</Button>}
    </div>
  );
  const text = (key: 'success' | 'firstProof' | 'notProposed', label: string, placeholder: string) => (
    <label className="block">
      <span className="text-sm font-semibold text-domino-ink">{label}</span>
      <Textarea
        value={proposal[key]}
        onChange={(event) => onChange({ ...sheet, proposal: { ...proposal, [key]: event.target.value } })}
        rows={2}
        placeholder={placeholder}
        className="mt-1 text-sm"
      />
    </label>
  );

  return (
    <section className="space-y-5">
      <div>
        <h3 className="font-display text-lg font-semibold text-domino-ink">Proposition</h3>
        <p className="text-xs text-domino-inkSoft">Ce que Domino doit être chez vous : sa configuration, ses applications et la façon de s’en servir.</p>
      </div>

      <div>
        {header('configuration', 'Configuration', 'Profil, objectifs, CRM, connexions, documents.')}
        <ul className="divide-y divide-domino-cardBorder/60">
          {proposal.configuration.map((entry) => (
            <Entry key={entry.id} decision={entry.decision} onDecision={decide('configuration', entry.id)} onRemove={remove('configuration', entry.id)}>
              <span className="font-medium">{CONFIGURATION_AREA_LABELS[entry.area]} · </span>{entry.text}
              {entry.why ? <p className="text-xs text-domino-inkSoft">{entry.why}</p> : null}
            </Entry>
          ))}
        </ul>
        {adding === 'configuration' && <ConfigurationForm onCancel={() => setAdding(null)} onAdd={(entry) => { onChange(addProposalEntry(sheet, 'configuration', entry)); setAdding(null); }} />}
      </div>

      <div>
        {header('apps', 'Applications', 'Une à trois, chacune liée à un goulot chiffré, une mesure et un jeu de vérité.')}
        <ul className="divide-y divide-domino-cardBorder/60">
          {proposal.apps.map((app) => (
            <Entry key={app.id} decision={app.decision} onDecision={decide('apps', app.id)} onRemove={remove('apps', app.id)}>
              <p className="font-medium">{app.title}</p>
              <p className="text-xs text-domino-inkSoft">Goulot : {app.bottleneck}{app.cost ? ` · Coût : ${app.cost}` : ''}</p>
              <p className="text-xs text-domino-inkSoft">Mesure : {app.measure}{app.truthSet ? ` · Jeu de vérité : ${app.truthSet}` : ''}</p>
              {app.expected ? <p className="text-xs text-domino-inkSoft">Résultat attendu : {app.expected}</p> : null}
            </Entry>
          ))}
        </ul>
        {adding === 'apps' && <AppForm onCancel={() => setAdding(null)} onAdd={(entry) => { onChange(addProposalEntry(sheet, 'apps', entry)); setAdding(null); }} />}
      </div>

      <div>
        {header('usage', 'Utilisation de Domino', 'Le rituel : qui fait quoi, où, et à quel rythme.')}
        <ul className="divide-y divide-domino-cardBorder/60">
          {proposal.usage.map((entry) => (
            <Entry key={entry.id} decision={entry.decision} onDecision={decide('usage', entry.id)} onRemove={remove('usage', entry.id)}>
              <span className="font-medium">{USAGE_RHYTHM_LABELS[entry.rhythm]} · {USAGE_PLACE_LABELS[entry.place]}</span>
              <p>{entry.who} : {entry.what}</p>
            </Entry>
          ))}
        </ul>
        {adding === 'usage' && <UsageForm onCancel={() => setAdding(null)} onAdd={(entry) => { onChange(addProposalEntry(sheet, 'usage', entry)); setAdding(null); }} />}
      </div>

      {text('success', 'Réussite', 'Vérifiable et daté : « chaque commercial a traité cinq comptes de bout en bout d’ici le … »')}
      {text('firstProof', 'Première preuve', 'Quoi, sur quelles données, quand.')}
      {text('notProposed', 'Ce qui n’est pas proposé', 'Et pourquoi.')}
    </section>
  );
};
